/**
 * 顧客の詳細（縦串9、028、docs/23）。/partners/:id
 *   名前と宛名（名前の横に「会社情報取得」）／住所と連絡先／番号（法人番号・登録番号・番号チェック）／請求（支払条件・振込手数料）／
 *   担当者（複数）／支店・営業所／社内メモ／この顧客の見積・請求書／「使う」のスイッチ
 * 顧客そのものの欄は1つの「{customer}を保存」で PUT /partners/:id（省いた欄は変えない）。担当者・支店は行ごとにその場で保存
 * S-00009：「使わない」は右の列の一番下のスイッチ「使う」（ON＝使う）。切ると POST /partners/:id/archive、入れると …/unarchive。
 *   すぐ戻せる状態なので確かめなし・画面に留まる（前は確かめの帯→一覧へ移っていた）。外注先も同じ画面
 * 9/29（#92、docs/35）：欄の下の説明は節の見出しの「？」へ（請求・社内メモ・「使わない」の帯）。「法人番号・登録番号は入力できます。」は無くした（決め10）
 * #213（docs/40 §3）：社内メモの上に「{referrer}」の枠（パートナー手数料の会社だけ）＝スイッチ → 手数料 ％（小数2桁・0 より大きく 90 未満）・備考 20字（暗号化）。
 *   保存は同じ「{customer}を保存」。パートナー手数料を OFF にした会社は欄を隠し、送らない（登録は残る）
 * #223 の2回目（docs/89 §6）：紙の書式の下に「送付状」の既定（テンプレート・窓付封筒。iOS 風の行で、選んだ・押した瞬間に保存＝PUT /partners/:id/cover。だれでも）。
 *   2列の下（幅いっぱい）に送付の記録の一覧（components/Deliveries.tsx）
 */
import {
  CONFLICT_MSG, rebaseFields,
  HONORIFIC_LABEL, PARTNER_MSG, QUALIFIED_STATUS_LABEL, TRANSFER_FEE_LABEL, TRANSFER_FEE_NOTE, DEFAULT_PAYMENT_TERMS, TERM_DAY_LABEL, TERM_MONTH_LABEL, formatZip, paymentTermsLabel, stripCorporateWords,
  type CorporateInfo, type Honorific, type Partner, type PartnerBranch, type PartnerBranchInput, type PartnerContact, type PartnerContactInput, type PartnerKind, type PartnerUpdate, type PaymentTerms, type TermDay, type TermMonth, type TransferFeeBearer,
  type ProjectGroupList,
  PAPER_MSG, type PaperPresetList,
  ROUNDING_LABEL,
  ROUNDING_MSG,
  RoundingDirection,
  type RoundingSettings,
  REFERRAL_MEMO_MAX,
  REFERRAL_MSG,
  COVER_LETTER_MSG, MAIL_MODE_LABEL, MAIL_MSG,
  VENDOR_PAYMENT_MSG, ACCEPTANCE_MSG, DELIVERY_NOTE_MSG,
  BILLING_MODE_LABEL, CLOSING_MSG, type BillingMode,
  INSTALLMENT_COUNTS, INSTALLMENT_INTERVALS, PAY_SCHEDULE_MSG, installmentPlanLabel, intervalLabel, paymentDueOf, scheduleDatesOf, type InstallmentPlan,
  type CoverLetterSettings,
  type PartnerOrderInfo,
  PARTNER_PICKER_MSG,
  partnerCodeOf,
} from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { AddressFields } from "../components/AddressFields.jsx";
import { PhoneFields } from "../components/PhoneFields.jsx";
import { CorporateLookupButton, CorporateLookupDialog, NtaSource, type CorporatePatch } from "../components/CorporateLookup.jsx";
import { Icon } from "../components/Shell.jsx";
import { ErrorText, Field, Help, Switch, guardComposingEnter, useToast } from "../components/ui.jsx";
import { t, useLabels } from "../labels.js";
import { uses } from "../core/uses.js";
import { Segmented } from "../components/Segmented.jsx";
import { yen } from "../lib/num.js";
import { fmtDate } from "./Partners.jsx";
import { ROUNDING_KEY } from "./SettingsRounding.jsx";
import { Slot, hasModule, useSlotPreload } from "../core/slots.jsx";
import { PctField } from "../components/ReferralParts.jsx";
import { RowMenu } from "../components/RowMenu.jsx";
import { usePcOnly } from "../lib/pcOnly.js";
import { GroupList, GroupSelectRow, GroupSwitchRow } from "../components/GroupList.jsx";
import { DeliveriesSection } from "../components/Deliveries.jsx";
import { PartnerOrderSection } from "./orders/PartnerOrderSection.jsx";
import { PartnerPaymentsSection } from "./purchases/VendorPayments.jsx";

type Form = {
  name: string; shortName: string; nameKana: string; kind: PartnerKind; honorific: Honorific;
  phone: string; fax: string; postalCode: string; address: string; building: string;
  corporateNumber: string; invoiceRegistrationNo: string; transferFeeBearer: TransferFeeBearer; memo: string;
  /** #288：顧客コード・仕入先コード（任意の英数字。空＝無し） */
  customerCode: string; vendorCode: string;
  /** #213：パートナー（手数料の率％・備考 20字） */
  isReferrer: boolean; referralPct: number | null; referralMemo: string;
};
/** パートナーの3欄（パートナー手数料を OFF にした会社は送らない＝404 にしない・登録を変えない） */
const REFERRAL_KEYS = ["isReferrer", "referralPct", "referralMemo"] as const;
const fromPartner = (p: Partner): Form => ({
  name: p.name, shortName: p.shortName || stripCorporateWords(p.name), nameKana: p.nameKana, kind: p.kind, honorific: p.honorific as Honorific,
  phone: p.phone, fax: p.fax, postalCode: p.postalCode, address: p.address, building: p.building,
  corporateNumber: p.corporateNumber, invoiceRegistrationNo: p.invoiceRegistrationNo, transferFeeBearer: p.transferFeeBearer, memo: p.memo,
  customerCode: p.customerCode, vendorCode: p.vendorCode,
  isReferrer: p.isReferrer, referralPct: p.referralPct, referralMemo: p.referralMemo,
});
const same = (a: Form, b: Form) => (Object.keys(a) as (keyof Form)[]).every((k) => a[k] === b[k]);
const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
};
/** 日付だけ（番号チェックの「確認日」。2026-10-13 射場さん） */
const fmtDateJa = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
};
export const HONORIFICS: Honorific[] = ["御中", "様", "殿", ""];

/**
 * 顧客の版つきの書き込み（#202、docs/82 §2）：読んだときの版を付けて送り、ほかで変わっていたら（409 PARTNER_STALE）
 * 最新を読んで、同じ操作を最新の版でもう一度送る（この画面の操作を最新に重ねる。本体の欄は send が latest で重ね直す）。
 * merged＝重ね直して送った（画面は CONFLICT_MSG.autoMerged を知らせる）
 */
async function withPartnerVersion(id: string, version: string, send: (version: string, latest?: Partner) => Promise<Partner>): Promise<{ p: Partner; merged: boolean }> {
  try {
    return { p: await send(version), merged: false };
  } catch (e) {
    if (!(e instanceof ApiError && e.code === "PARTNER_STALE")) throw e;
    const latest = await api<Partner>("GET", `/partners/${id}`);
    return { p: await send(latest.updatedAt, latest), merged: true };
  }
}
/** 「？」の中身（docs/10 §5.1.1。2026-09-23 射場さん：説明の文は「？」に要約） */
export const SHORT_NAME_HELP = "社内で使う短い呼び名です。「株式会社」などを外した名前や、ふだんの呼び名を入れます。「名前で探す」でも引けます。見積書・請求書の紙には出ません。";
export const CONTACTS_HELP = "氏名・携帯・メールは暗号化して保存します。見積・請求書で選ぶと、宛名が「部署 氏名 様」になります（携帯・メールは紙に出ません）。";
/** 例の宛名は製品ごと（#142 段6 No.50：{pw:branchExample}＝○○建設 大阪支店／○○商事 大阪支店）。使う所で t() を通す */
export const BRANCHES_HELP = "宛先を支店ごとに分けたいときに登録します。見積・請求書で選ぶと「{pw:branchExample} 御中」と出ます。支払条件・登録番号は会社に1つです。";

import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";

export function PartnerPage() {
  const { id = "" } = useParams();
  const L = useLabels();
  const me = useMe();
  const canLookup = !!me.data?.features.corporateLookup;
  /** パートナー手数料（#213）：モジュール referral の会社だけ枠を出す */
  const referralOn = hasModule(me.data, "referral");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const pcOnly = usePcOnly();
  const q = useQuery({ queryKey: ["partner", id], queryFn: () => api<Partner>("GET", `/partners/${id}`), retry: false });
  // 入金予定（#267 3b）の先読み：届いてから入金予定と「送付の記録」を描く
  const recvReady = useSlotPreload("partner.receivables", me.data, { partnerId: id });
  // 案件（033）。右列の「{project}」の段。1つも無ければ段を出さない
  const groups = useQuery({ queryKey: ["groups", id], queryFn: () => api<ProjectGroupList>("GET", `/partners/${id}/groups`), enabled: !!id });
  /**
   * {order}の節（#233）の中身。節の中で読むと、届いたときに節の背が伸びて下の節（番号・パートナー）が押す直前にずれるので、
   * 同じ問い合わせをここで先に取り、そろってから一度に描く（#179、docs/70。節の部品は同じ鍵の控えを使う）
   */
  const orderOn = !!q.data && (q.data.partnerType === "vendor" || q.data.partnerType === "both") && !!me.data?.tenant.modules.includes("sales");
  const orderInfo = useQuery({ queryKey: ["partner-order", id], queryFn: () => api<PartnerOrderInfo>("GET", `/partners/${id}/order`), enabled: orderOn });
  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | undefined>();
  const [lookup, setLookup] = useState(false);
  const [contactDlg, setContactDlg] = useState<{ open: boolean; edit: PartnerContact | null }>({ open: false, edit: null });
  const [branchDlg, setBranchDlg] = useState<{ open: boolean; edit: PartnerBranch | null }>({ open: false, edit: null });
  /**
   * 欄の元（#202）：いまの欄がどの版の顧客から作られたか。読み直し（画面に戻ったとき・保存の答え・ほかの書き込みの答え）で顧客が変わったら、
   * この画面で修正中の欄だけを残して新しい顧客に重ねる（前の版のままの欄で、ほかの利用者の変更を上書きしない）。保存の版もここから
   */
  const formBase = useRef<Partner | null>(null);
  useEffect(() => {
    const now = q.data;
    if (!now || formBase.current === now) return;
    const prev = formBase.current;
    formBase.current = now;
    setForm((f) => (f && prev ? rebaseFields(fromPartner(prev), f, fromPartner(now)) : fromPartner(now)));
  }, [q.data]);
  const loc = useLocation();

  /** サーバーの答えで顧客を入れ替える（本体の欄は上の formBase の重ね直しで、修正中の分が残る） */
  const applyServer = (p: Partner) => {
    qc.setQueryData(["partner", id], p);
    void qc.invalidateQueries({ queryKey: ["partners"] });
  };
  /**
   * 本体の欄を保存（PUT /partners/:id）。欄の元の版を付けて送り、ほかで変わっていたら最新に「この画面で修正した欄だけ」を重ねて送り直す（#202）
   */
  const putForm = (v: Form) => {
    const base = formBase.current ?? q.data!;
    /** #213：パートナー手数料の会社だけパートナーの3欄を送る（率は空なら 0） */
    const body = (f: Form): PartnerUpdate => {
      const { isReferrer, referralPct, referralMemo, ...rest } = f;
      return referralOn ? { ...rest, isReferrer, referralPct: referralPct ?? 0, referralMemo } : rest;
    };
    return withPartnerVersion(id, base.updatedAt, (version, latest) =>
      api<Partner>("PUT", `/partners/${id}`, { ...body(latest ? rebaseFields(fromPartner(base), v, fromPartner(latest)) : v), version } satisfies PartnerUpdate),
    );
  };
  /**
   * 保存の知らせ。保存のときに番号チェックが流れたら結果も一緒に（docs/23 §4）。
   * 国税庁に照会できなかったら注意の通知に「再試行」（POST …/check-registration だけ。顧客はもう保存してある）
   */
  const savedNotice = (p: Partner, head: string) => {
    if (p.autoCheck === "failed") return toast(`${head}${PARTNER_MSG.autoCheckFailed}`, { label: PARTNER_MSG.autoCheckRetry, onClick: () => check.mutate(undefined) }, "warn", "autocheck-failed");
    toast(p.autoCheck === "checked" && p.qualified ? `${head}番号チェック：${QUALIFIED_STATUS_LABEL[p.qualified.status]}` : head);
  };
  const save = useMutation({
    mutationFn: (v: Form) => putForm(v),
    onSuccess: ({ p, merged }) => {
      setForm(fromPartner(p));
      setErrors({});
      applyServer(p);
      void qc.invalidateQueries({ queryKey: ["estimate"] });
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      savedNotice(p, merged ? `${CONFLICT_MSG.autoMerged}。` : `${p.name} ${p.honorific} を保存しました。`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.problem.errors) {
        setErrors(e.problem.errors);
        const first = Object.keys(e.problem.errors)[0];
        if (first) document.getElementById(`pd_${first}`)?.focus();
        return;
      }
      setServerError(messageOf(e));
    },
  });
  const check = useMutation({
    /** v があれば打った番号を先に保存してから確かめる（保存で番号チェックが流れたら、それをそのまま使う＝2回照会しない）。「再試行」は v なし */
    mutationFn: async (v: Form | undefined) => {
      if (v) {
        const saved = (await putForm(v)).p;
        if (saved.autoCheck) return saved;
      }
      return api<Partner>("POST", `/partners/${id}/check-registration`);
    },
    onSuccess: (p) => {
      setForm(fromPartner(p));
      setErrors({});
      applyServer(p);
      if (p.autoCheck === "failed") return savedNotice(p, "");
      toast(p.qualified ? `番号チェック：${QUALIFIED_STATUS_LABEL[p.qualified.status]}` : "番号チェックを保存しました。");
    },
    onError: (e) => {
      if (e instanceof ApiError && e.problem.errors) {
        setErrors(e.problem.errors);
        return;
      }
      setServerError(messageOf(e));
    },
  });
  const archive = useMutation({
    mutationFn: async (on: boolean) => (await withPartnerVersion(id, q.data!.updatedAt, (version) => api<Partner>("POST", `/partners/${id}/${on ? "archive" : "unarchive"}`, { version }))).p,
    // 画面に留まる（S-00009）。スイッチがそのまま戻す口なので、トーストに「戻す」は付けない
    onSuccess: (p, on) => {
      applyServer(p);
      toast(on ? `${p.name} ${p.honorific} を「使わない」にしました。一覧・候補には出ません。` : `${p.name} ${p.honorific} を戻しました。`);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  // 登録（/partners/new）から来たときの知らせ（state で受け取り、1回だけ出す）。登録のときの番号チェックが国税庁に届かなかったら「再試行」も
  useEffect(() => {
    const st = loc.state as { toast?: string; checkFailed?: boolean } | null;
    if (st?.toast) {
      if (st.checkFailed) toast(`${st.toast}${PARTNER_MSG.autoCheckFailed}`, { label: PARTNER_MSG.autoCheckRetry, onClick: () => check.mutate(undefined) }, "warn", "autocheck-failed");
      else toast(st.toast);
      nav(loc.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ{customer}（外注先だけの相手は外注先）の一覧。直しかけは確かめる
  const back = useBackNav(
    q.data?.partnerType === "vendor" ? { to: "/partners?type=vendor", label: L.vendor } : { to: "/partners", label: L.customer },
    !!q.data && !!form && !same(form, fromPartner(q.data)),
  );
  usePageName(q.data?.name, q.data ? (q.data.partnerType === "vendor" ? L.vendor : L.customer) : null);
  if (q.isError) {
    return (
      <>
        <div className="hrow">
          <h1>{L.customer}</h1>
          <span className="sp" />
          <Link className="btn" to="/partners">一覧へ</Link>
        </div>
        <ErrorText>{messageOf(q.error)}</ErrorText>
      </>
    );
  }
  if (!q.data || !form || (orderOn && orderInfo.isPending)) return <p className="hint">読み込んでいます…</p>;
  const p = q.data;
  const who = p.partnerType === "vendor" ? L.vendor : L.customer;
  const dirty = !same(form, fromPartner(p));
  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const submit = () => {
    setServerError(undefined);
    if (!form.name.trim()) {
      setErrors({ name: t(PARTNER_MSG.nameRequired) });
      document.getElementById("pd_name")?.focus();
      return;
    }
    if (!form.shortName.trim()) {
      setErrors({ shortName: PARTNER_MSG.shortNameRequired });
      document.getElementById("pd_shortName")?.focus();
      return;
    }
    save.mutate(form);
  };
  /** 会社情報取得の「反映」：チェックした項目だけ（会社名を入れたときは法人・敬称も合わせる） */
  const applyCorporate = (patch: CorporatePatch, c: CorporateInfo) => {
    set({ ...patch, ...(patch.name !== undefined ? { kind: "corporate" as const, honorific: form.kind === "corporate" ? form.honorific : "御中" } : {}) });
    setLookup(false);
    toast(`国税庁の登録「${c.name}」を入れました。確かめて「${who}を保存」を押してください。`);
  };
  const firstError = Object.values(errors)[0];
  const H2 = ({ children }: { children: React.ReactNode }) => <h2 style={{ margin: "0 0 10px", fontSize: "var(--fs-19)" }}>{children}</h2>;
  const sec = { padding: "16px 22px 18px", marginBottom: 14 } as const;
  const archived = !!p.archivedAt;

  return (
    <>
      <BackButton back={back} />
      {back.dialog}
      <div className="hrow" style={{ marginBottom: 14, flexWrap: "wrap", alignItems: "center", rowGap: 8 }}>
        <h1 style={{ fontSize: "var(--fs-24)", margin: 0 }}>
          {p.name} <span className="muted" style={{ fontSize: "var(--fs-18)" }}>{p.honorific}</span>
        </h1>
        <span className="chip">{p.kind === "corporate" ? "法人" : "個人"}</span>
        {/* 「使わない」は見出しの右の朱のチップ（#168：前は朱の地の帯） */}
        {archived ? (
          <>
            <span className="chip shu" title={`この${who}は「使わない」になっています（${fmtDateTime(p.archivedAt!)}）`} data-testid="partner-archived">使わない</span>
            <Help testId="help-archived" label="「使わない」の説明" text={`この${who}は「使わない」になっています（${fmtDateTime(p.archivedAt!)}）。一覧・候補には出ません。出した見積・請求書は残っています。「使う」のスイッチを入れると戻ります。`} />
          </>
        ) : null}
        <span className="sp" />
        {!archived ? <Link className="btn" to={`/groups/new?client=${p.id}`}>新しい{L.project}</Link> : null}
        <Link className="btn primary" to={`/estimates/new?client=${p.id}`}>
          <Icon k="plus" />
          見積作成
        </Link>
        {/* 単体送付状（#223、docs/89 §5.3・§15 の12）：PC だけ（スマホでは入口を出さない） */}
        {!pcOnly.hidden(`/cover-letters/new?partner=${p.id}`) ? <RowMenu label={`${p.name}のほかの動き`} testId="partner-more" items={[{ label: COVER_LETTER_MSG.create, to: `/cover-letters/new?partner=${p.id}`, testId: "partner-cover-letter" }]} /> : null}
      </div>

      <div className="pdgrid">
      <form
        className="pdmain"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {/* ---- 名前と宛名 ---- */}
        <section className="paper" style={sec}>
          <H2>名前と宛名</H2>
          <div className="row" style={{ alignItems: "flex-end" }}>
            {/* 会社情報取得（国税庁）は名前の欄の中の右端（2026-10-14 案A、Figma「会社情報取得（10/14）」①。docs/23 §4） */}
            <Field id="pd_name" label="名前（会社名、または個人のお名前）" style={{ flex: "2 1 300px" }} end={canLookup ? <CorporateLookupButton onClick={() => setLookup(true)} /> : undefined}>
              <input id="pd_name" className="inp" value={form.name} onKeyDown={guardComposingEnter} onChange={(e) => set({ name: e.target.value })} aria-invalid={!!errors.name} />
            </Field>
            <Field id="pd_shortName" label="略称" style={{ flex: "1 1 180px" }} help={<Help testId="help-shortname" label="略称の説明" text={SHORT_NAME_HELP} />}>
                <input id="pd_shortName" className="inp" value={form.shortName} required aria-required="true" onKeyDown={guardComposingEnter} onChange={(e) => set({ shortName: e.target.value })} aria-invalid={!!errors.shortName} />
              </Field>
          </div>
          {/* フリガナは名前の下の行（2026-09-23 射場さん） */}
          <div className="row" style={{ marginTop: 12, alignItems: "flex-end" }}>
            <Field id="pd_nameKana" label="フリガナ（一覧の並び順）" style={{ flex: "2 1 300px" }}>
              <input id="pd_nameKana" className="inp" value={form.nameKana} onKeyDown={guardComposingEnter} onChange={(e) => set({ nameKana: e.target.value })} aria-invalid={!!errors.nameKana} />
            </Field>
            {/* 顧客コード・仕入先コード（#288、docs/101 §3）：役割があるときだけ。フリガナの行の空きに置く（画面の高さを変えない） */}
            {p.partnerType !== "vendor" ? (
              <Field id="pd_customerCode" label={PARTNER_PICKER_MSG.customerCode} style={{ flex: "1 1 180px" }}>
                <input id="pd_customerCode" className="inp code" value={form.customerCode} maxLength={40} autoCapitalize="characters" onKeyDown={guardComposingEnter} onChange={(e) => set({ customerCode: e.target.value })} onBlur={() => set({ customerCode: partnerCodeOf(form.customerCode) })} aria-invalid={!!errors.customerCode} data-testid="partner-customer-code" />
              </Field>
            ) : null}
            {p.partnerType !== "client" ? (
              <Field id="pd_vendorCode" label={PARTNER_PICKER_MSG.vendorCode} style={{ flex: "1 1 180px" }}>
                <input id="pd_vendorCode" className="inp code" value={form.vendorCode} maxLength={40} autoCapitalize="characters" onKeyDown={guardComposingEnter} onChange={(e) => set({ vendorCode: e.target.value })} onBlur={() => set({ vendorCode: partnerCodeOf(form.vendorCode) })} aria-invalid={!!errors.vendorCode} data-testid="partner-vendor-code" />
              </Field>
            ) : null}
          </div>
          <div className="row" style={{ marginTop: 12, alignItems: "flex-end" }}>
            <div className="field" style={{ flex: "0 0 auto" }}>
              <label>法人／個人</label>
              <div className="seg" role="group" aria-label="法人／個人">
                {(["corporate", "individual"] as PartnerKind[]).map((k) => (
                  <button key={k} type="button" aria-pressed={form.kind === k} onClick={() => set({ kind: k, honorific: k === "individual" ? "様" : "御中" })}>
                    {k === "corporate" ? "法人" : "個人"}
                  </button>
                ))}
              </div>
            </div>
            <div className="field" style={{ flex: "0 0 auto" }}>
              <label>敬称（紙の宛名）</label>
              <div className="seg" role="group" aria-label="敬称">
                {HONORIFICS.map((h) => (
                  <button key={h || "none"} type="button" aria-pressed={form.honorific === h} onClick={() => set({ honorific: h })}>
                    {HONORIFIC_LABEL[h]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ---- 住所と連絡先 ---- */}
        <section className="paper" style={sec}>
          <H2>住所と連絡先</H2>
          <AddressFields value={{ postalCode: form.postalCode, address: form.address, building: form.building }} onChange={(v) => set(v)} ids={{ zip: "pd_postalCode", address: "pd_address", building: "pd_building" }} />
          {/* 電話・FAX は縦に並べる（2026-09-19 射場さん） */}
          <div className="tel-stack" style={{ marginTop: 12 }}>
            <PhoneFields id="pd_phone" label="TEL" value={form.phone} onChange={(phone) => set({ phone })} invalid={!!errors.phone} />
            <PhoneFields id="pd_fax" label="FAX" value={form.fax} onChange={(fax) => set({ fax })} invalid={!!errors.fax} />
          </div>
        </section>

        {/* ---- 請求 ---- */}
        <section className="paper" style={sec}>
          <H2>
            請求
            <Help testId="help-billing" label="請求の説明" text={`振込手数料が「先方」なら、請求書を起こすとき備考に「${TRANSFER_FEE_NOTE}」が入ります（請求書ごとに消せます）。`} />
          </H2>
          <div className="field" style={{ marginBottom: 12 }}>
            <label>支払条件（請求書のお支払期限をここから計算）</label>
            <TermsEditor
              p={p}
              sales={!!me.data?.tenant.modules.includes("sales")}
              onSaved={(np, merged) => {
                applyServer(np);
                void qc.invalidateQueries({ queryKey: ["invoice"] });
                void qc.invalidateQueries({ queryKey: ["closing"] });
                toast(merged ? CONFLICT_MSG.autoMerged : `支払条件を「${np.paymentTerms ? paymentTermsLabel(np.paymentTerms as PaymentTerms) : "既定"}」にしました。`);
              }}
            />
          </div>
          {/* 消費税の端数処理（#108）：紙の書式と同じ「名前＋セレクト」の1行（浮く名前の欄にしない＝選んだらすぐ保存） */}
          <div className="row" style={{ gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
            <label htmlFor="pd_taxRounding" style={{ fontSize: "var(--fs-15)", color: "var(--ink2)" }}>消費税の端数処理</label>
            <TaxRoundingEditor p={p} admin={me.data?.user.role === "admin"} onSaved={(np, merged) => { applyServer(np); qc.removeQueries({ queryKey: ["estimate"] }); toast(merged ? CONFLICT_MSG.autoMerged : ROUNDING_MSG.partnerSaved); }} onError={(m) => toast(m, undefined, "error")} />
            <Help testId="help-tax-rounding" text={`${L.customer}から指定がある場合のみ変更。新規の見積から適用。変更は管理者のみ。明細・率の行は 設定 → 端数処理 で会社ごとに設定。`} />
          </div>
          {/* 税を確定する書類（#266、docs/96 決めごと13）：sales の会社で使う機能「{deliveryNote}」がオンのとき。選んだらすぐ保存・管理者だけ */}
          {me.data?.tenant.modules.includes("sales") && uses(me.data, "deliveryNote") ? (
            <div className="row" style={{ gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
              <label htmlFor="pd_taxOn" style={{ fontSize: "var(--fs-15)", color: "var(--ink2)" }}>{DELIVERY_NOTE_MSG.taxOnLabel}</label>
              <TaxOnEditor p={p} admin={me.data?.user.role === "admin"} onSaved={(np, merged) => { applyServer(np); toast(merged ? CONFLICT_MSG.autoMerged : `${DELIVERY_NOTE_MSG.taxOnLabel}を「${np.taxOn === "delivery" ? L.deliveryNote : "請求書"}」にしました。`); }} onError={(m) => toast(m, undefined, "error")} />
              <Help testId="help-tax-on" text={t(DELIVERY_NOTE_MSG.taxOnHelp)} />
            </div>
          ) : null}
          {/* {acceptance}の既定（#266 1b、スイッチの2段目・Figma ⑥-2）：使う機能「{acceptance}」の会社だけ。会社に従う／オン／オフ・選んだらすぐ保存 */}
          {me.data?.tenant.modules.includes("sales") && uses(me.data, "acceptance") ? (
            <div className="row" style={{ gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
              <span id="pd_acceptance_l" style={{ fontSize: "var(--fs-15)", color: "var(--ink2)" }}>{L.acceptance}</span>
              <AcceptanceDefaultEditor p={p} onSaved={(np, merged) => { applyServer(np); void qc.invalidateQueries({ queryKey: ["delivery-note"] }); toast(merged ? CONFLICT_MSG.autoMerged : t(ACCEPTANCE_MSG.partnerSaved)); }} onError={(m) => toast(m, undefined, "error")} />
              <Help testId="help-partner-acceptance" text={t(ACCEPTANCE_MSG.partnerHelp)} />
            </div>
          ) : null}
          <div className="field">
            <label>振込手数料の負担</label>
            <div className="seg" role="group" aria-label="振込手数料の負担">
              {(["customer", "us"] as TransferFeeBearer[]).map((b) => (
                <button key={b} type="button" aria-pressed={form.transferFeeBearer === b} onClick={() => set({ transferFeeBearer: b })}>
                  {TRANSFER_FEE_LABEL[b]}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ---- 紙の書式（#78、044） ---- */}
        <section className="paper" style={sec}>
          <H2>
            紙の書式
            <Help text={t(`この${L.customer}に出す見積書・請求書の書式です。{pw:formsFrom}から指定の様式がある${L.customer}などに使います。「会社の既定に従う」なら 設定 → 帳票デザイン の書式で出ます。出すときに紙の画面で選び直すこともでき、出した紙は固まっているので、ここを変えても変わりません。`)} />
          </H2>
          <PaperEditor p={p} admin={me.data?.user.role === "admin"} onSaved={(np, merged) => { applyServer(np); qc.removeQueries({ queryKey: ["estimate"] }); qc.removeQueries({ queryKey: ["invoice"] }); toast(merged ? CONFLICT_MSG.autoMerged : PAPER_MSG.saved); }} onError={(m) => toast(m, undefined, "error")} />
        </section>

        {/* ---- 送付状の既定（#223 の2回目、docs/89 §6） ---- */}
        <section className="paper" style={sec} data-testid="partner-cover">
          <H2>
            {COVER_LETTER_MSG.partnerSection}
            <Help testId="help-partner-cover" label={`${COVER_LETTER_MSG.partnerSection}の説明`} text={t(COVER_LETTER_MSG.partnerHelp)} />
          </H2>
          <CoverDefaultsEditor p={p} disabled={archived} onSaved={(np, merged) => { applyServer(np); qc.removeQueries({ queryKey: ["cover-draft"] }); toast(merged ? CONFLICT_MSG.autoMerged : COVER_LETTER_MSG.partnerSaved); }} onError={(m) => toast(m, undefined, "error")} />
        </section>

        {/* ---- {order}（#233、docs/93 §4.6）：{vendor}（外注先・両方）でモジュール sales の会社だけ。承諾方法・種別・電子でよいと確認済み ---- */}
        {(p.partnerType === "vendor" || p.partnerType === "both") && me.data?.tenant.modules.includes("sales") ? (
          <section className="paper" style={sec} data-testid="partner-order-section">
            <H2>{L.order}</H2>
            <PartnerOrderSection partnerId={p.id} disabled={archived} />
          </section>
        ) : null}

        {/* ---- 番号（法人番号・登録番号） ---- */}
        <section className="paper" style={sec}>
          <H2>法人番号と登録番号（インボイス）</H2>
          <div className="row" style={{ alignItems: "flex-end" }}>
            <Field id="pd_corporateNumber" label="法人番号（13桁）" style={{ flex: "1 1 220px" }}>
              <input id="pd_corporateNumber" className="inp num" inputMode="numeric" value={form.corporateNumber} onKeyDown={guardComposingEnter} onChange={(e) => set({ corporateNumber: e.target.value })} aria-invalid={!!errors.corporateNumber} />
            </Field>
            <Field id="pd_invoiceRegistrationNo" label="登録番号（T＋13桁）" style={{ flex: "1 1 240px" }}>
              <input id="pd_invoiceRegistrationNo" className="inp num" value={form.invoiceRegistrationNo} placeholder={form.corporateNumber ? `T${form.corporateNumber}` : undefined} onKeyDown={guardComposingEnter} onChange={(e) => set({ invoiceRegistrationNo: e.target.value })} aria-invalid={!!errors.invoiceRegistrationNo} />
            </Field>
            {canLookup ? (
              <button type="button" className="small" disabled={check.isPending || save.isPending || archived} onClick={() => check.mutate(form)} title="国税庁の公表サイトで、適格請求書発行事業者として登録されているかを確かめます">
                {check.isPending ? "確かめています…" : "番号チェック"}
              </button>
            ) : null}
          </div>
          {p.qualified || canLookup ? (
          <div className="hint" style={{ marginTop: 8 }} data-testid="qualified-status">
            {p.qualified ? (
              <>
                <b className={p.qualified.status === "registered" ? "" : "shu"}>{QUALIFIED_STATUS_LABEL[p.qualified.status]}</b>
                {p.qualified.registeredOn ? `　登録日 ${p.qualified.registeredOn}` : ""}
                {p.qualified.revokedOn ? `　${p.qualified.status === "revoked" ? "取消日" : "失効日"} ${p.qualified.revokedOn}` : ""}
                {p.qualified.ntaName ? `　国税庁の名称：${p.qualified.ntaName}` : ""}
                {p.qualified.ntaName && p.qualified.ntaName !== p.name ? <span className="shu">（{L.customer}の名前と違います）</span> : null}
                {/* 行頭の全角の空白は JSX が捨てるので文字で（前の項目と離す） */}
                {`　確認日 ${fmtDateJa(p.qualified.checkedAt)}`}
              </>
            ) : (
              "登録番号はまだ確かめていません。"
            )}
          </div>
          ) : null}
          {p.corporateFetchedAt ? <div className="hint" style={{ fontSize: 14 }}>法人番号から引いた日時 {fmtDateTime(p.corporateFetchedAt)}</div> : null}
          {canLookup ? <NtaSource style={{ marginTop: 6 }} /> : null}
        </section>

        {/* ---- パートナー（#213、docs/40 §3）：社内メモの上。パートナー手数料の会社だけ ---- */}
        {referralOn ? (
          <section className="paper" style={sec} data-testid="partner-referral">
            <H2>
              {t(REFERRAL_MSG.partnerSwitch)}
              <Help testId="help-partner-referral" label={`${t(REFERRAL_MSG.partnerSwitch)}の説明`} text={t(`見積の詳細で{referrer}を付けると、この手数料を単価に上乗せします（見積ごとに率を変えられます）。${REFERRAL_MSG.memoHelp}`)} />
            </H2>
            <div className="refrow">
              <Switch checked={form.isReferrer} label={t(REFERRAL_MSG.partnerSwitch)} testId="partner-referrer" onChange={(v) => set({ isReferrer: v })} bare />
              {form.isReferrer ? (
                <>
                  <label className="refpct" htmlFor="pd_referralPct">
                    {REFERRAL_MSG.pct}
                    <PctField id="pd_referralPct" value={form.referralPct} onCommit={(v) => set({ referralPct: v })} aria-invalid={!!errors.referralPct} data-testid="partner-referral-pct" />%
                  </label>
                  <Field id="pd_referralMemo" label={REFERRAL_MSG.memo} style={{ flex: "1 1 260px" }}>
                    <input id="pd_referralMemo" className="inp" value={form.referralMemo} maxLength={REFERRAL_MEMO_MAX} onKeyDown={guardComposingEnter} onChange={(e) => set({ referralMemo: e.target.value })} aria-invalid={!!errors.referralMemo} data-testid="partner-referral-memo" />
                  </Field>
                </>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* ---- 社内メモ ---- */}
        <section className="paper" style={sec}>
          <H2>
            社内メモ（お客様には出ません）
            <Help testId="help-partner-memo" label="社内メモの説明" text="暗号化して保存します。" />
          </H2>
          <textarea id="pd_memo" className="inp" rows={3} value={form.memo} onChange={(e) => set({ memo: e.target.value })} aria-invalid={!!errors.memo} style={{ minHeight: 96, resize: "vertical" }} />
        </section>

        <div className="savebar" data-notice-above="">
          <ErrorText>{serverError ?? firstError}</ErrorText>
          <span className="hint" style={dirty ? { color: "var(--shu)", fontWeight: 700 } : undefined}>{dirty ? "まだ保存していない変更があります" : ""}</span>
          <span className="sp" />
          <button type="button" className="btn quiet" disabled={!dirty || save.isPending} onClick={() => { setForm(fromPartner(p)); setErrors({}); setServerError(undefined); }}>元に戻す</button>
          <button type="submit" className="btn primary" disabled={save.isPending || check.isPending || archived} data-testid="partner-save">{who}保存</button>
        </div>
      </form>

      {/* ---- 右の列：この顧客のいま ---- */}
      <aside className="pdside">
        {/* 紙の宛名 */}
        <section className="paper">
          <h2 className="hint" style={{ fontWeight: 700, margin: "0 0 10px" }}>帳票の宛名</h2>
          <div className="pdaddressee">
            {/* 〒の次の行に住所（紙の発行者欄と同じ。2026-09-23 射場さん） */}
            {form.postalCode ? <span className="hint">〒{formatZip(form.postalCode)}</span> : null}
            {form.address || form.building ? <span className="hint">{[form.address, form.building].filter(Boolean).join(" ")}</span> : null}
            <span className="to">{form.name || "○○"}{form.honorific ? `　${form.honorific}` : ""}</span>
            {p.branches.length ? <span className="hint" style={{ paddingTop: 4 }}>{p.branches[0]!.name} 御中（支店を選んだとき）</span> : null}
          </div>
        </section>

        {/* 担当者（複数） */}
        <section className="paper">
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <h2>担当者<Help testId="help-contacts" label="担当者の説明" text={CONTACTS_HELP} /></h2>
            {p.contacts.length ? <span className="hint" style={{ fontSize: 14 }}>{p.contacts.length}人</span> : null}
          </div>
          {p.contacts.length === 0 ? (
            <p className="hint" style={{ margin: "0 0 8px" }}>まだ登録されていません。</p>
          ) : (
            p.contacts.map((c) => (
              <div className="pditem" key={c.id}>
                <div className="pdinfo">
                  <div>
                    <b>{c.name}</b>
                    {c.department || c.title ? <span className="hint" style={{ fontSize: 14, marginLeft: 6 }}>{[c.department, c.title].filter(Boolean).join("　")}</span> : null}
                    {c.isPrimary ? <span className="chip ai" style={{ marginLeft: 6 }}>主</span> : null}
                  </div>
                  {c.mobile ? <div className="hint num">携帯 {c.mobile}</div> : null}
                  {c.email ? <div className="hint num">{c.email}</div> : null}
                  {c.branchId ? <div className="hint">{p.branches.find((b) => b.id === c.branchId)?.name ?? "—"}</div> : null}
                </div>
                <button type="button" className="small" disabled={archived} onClick={() => setContactDlg({ open: true, edit: c })} aria-label={`${c.name} を編集`}>編集</button>
              </div>
            ))
          )}
          <button type="button" className="small ai" style={{ width: "100%", justifyContent: "center" }} disabled={archived} onClick={() => setContactDlg({ open: true, edit: null })}>担当者追加</button>
        </section>

        {/* 支店・営業所 */}
        <section className="paper">
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <h2>支店・営業所（宛先）<Help testId="help-branches" label="支店・営業所の説明" text={t(BRANCHES_HELP)} /></h2>
            {p.branches.length ? <span className="hint" style={{ fontSize: 14 }}>{p.branches.length}つ</span> : null}
          </div>
          {p.branches.length === 0 ? (
            <p className="hint" style={{ margin: "0 0 8px" }}>まだ登録されていません。</p>
          ) : (
            p.branches.map((b) => (
              <div className="pditem" key={b.id}>
                <div className="pdinfo">
                  <div><b>{b.name}</b></div>
                  {b.postalCode || b.address || b.building ? <div className="hint">{[b.postalCode ? `〒${formatZip(b.postalCode)}` : "", b.address, b.building].filter(Boolean).join(" ")}</div> : null}
                  {b.phone ? <div className="hint num">電話 {b.phone}</div> : null}
                  {b.fax ? <div className="hint num">FAX {b.fax}</div> : null}
                </div>
                <button type="button" className="small" disabled={archived} onClick={() => setBranchDlg({ open: true, edit: b })} aria-label={`${b.name} を編集`}>編集</button>
              </div>
            ))
          )}
          <button type="button" className="small ai" style={{ width: "100%", justifyContent: "center" }} disabled={archived} onClick={() => setBranchDlg({ open: true, edit: null })}>支店・営業所追加</button>
        </section>

        {/* 案件（033、docs/25）。1つも無ければ段を出さない */}
        {groups.data?.items.length ? (
          <section className="paper" data-testid="partner-groups">
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <h2>{L.project}</h2>
              <span className="hint" style={{ fontSize: 14 }}>{groups.data.items.length}件</span>
            </div>
            {groups.data.items.map((g) => (
              <Link key={g.id} className="pditem" style={{ textDecoration: "none", color: "inherit" }} to={`/groups/${g.id}`}>
                <div className="pdinfo">
                  <div><b>{g.name}</b>{g.completedAt ? <span className="chip" style={{ marginLeft: 8 }}>完了</span> : null}</div>
                  <div className="hint">
                    見積 <span className="num">{g.jobCount}</span>枚
                    {g.orderedCount ? <>　受注 <span className="num">{g.orderedCount}</span>件 <span className="num">{yen(g.orderedTotal)}</span> 円</> : null}
                  </div>
                </div>
                <span className="btn text" style={{ alignSelf: "center" }}>表示</span>
              </Link>
            ))}
          </section>
        ) : null}

        {/* この顧客の見積・請求書 */}
        <section className="paper">
          <h2>この{L.customer}の見積・請求書</h2>
          <Link className="small" style={{ width: "100%", justifyContent: "center" }} to={`/estimates?client=${p.id}`}>見積一覧表示</Link>
        </section>

        {/* 使う（右の列の一番下のスイッチ。S-00009：ON＝使う。切ると「使わない」＝一覧・候補に出ない。確かめなし・画面に留まる） */}
        <div>
          <section className="paper" data-testid="partner-use-box">
            <div className="row" style={{ alignItems: "center", gap: 8, flexWrap: "nowrap" }}>
              <span style={{ fontWeight: 700 }}>この{who}を使う</span>
              <Help testId="help-partner-use" label="「使う」の説明" text="切ると「使わない」になり、一覧・候補に出なくなります。出した見積・請求書は残ります。入れるといつでも戻ります。" />
              <span className="sp" />
              <Switch checked={!archived} onChange={(on) => archive.mutate(!on)} label={`この${who}を使う`} disabled={archive.isPending} testId="partner-use" bare />
            </div>
          </section>
          <p className="hint" style={{ margin: "8px 0 0", fontSize: 14, textAlign: "right" }}>登録日 {fmtDate(p.createdAt)}</p>
        </div>
      </aside>
      </div>

      {/* 支払（#233 3c、docs/93 §15.5）：{vendor}（外注先・両方）でモジュール sales の会社だけ。未払いの合計・「支払登録」（古い順に配分）・今月の支払・履歴。2列の下に幅いっぱい */}
      {(p.partnerType === "vendor" || p.partnerType === "both") && me.data?.tenant.modules.includes("sales") ? (
        <section className="paper pdpay" data-testid="partner-payments-section">
          <h2 className="pdpay-t">
            {VENDOR_PAYMENT_MSG.section}
            <Help testId="help-partner-payments" label={`${VENDOR_PAYMENT_MSG.section}の説明`} text={VENDOR_PAYMENT_MSG.helpHistory} />
          </h2>
          <PartnerPaymentsSection partner={p} />
        </section>
      ) : null}

      {/* 入金予定（#267 3b、docs/97 決めごと12）：{customer}（顧客・両方）で sales の会社だけ（部品 payments が差し込む）。2列の下に幅いっぱい */}
      {/* 入金予定の答えがそろってから、その節と下の「送付の記録」を描く（#179：あとから入って押す所をずらさない） */}
      {recvReady && (p.partnerType === "client" || p.partnerType === "both") && me.data ? <Slot name="partner.receivables" me={me.data} partner={p} /> : null}

      {/* 送付の記録（#223 の2回目）：2列の下に幅いっぱい */}
      {recvReady ? <DeliveriesSection filter={{ partner: p.id }} testId="partner-deliveries" /> : null}

      {canLookup ? <CorporateLookupDialog open={lookup} initialQuery={form.corporateNumber || form.name} onApply={applyCorporate} onClose={() => setLookup(false)} /> : null}
      {contactDlg.open ? <ContactDialog partner={p} edit={contactDlg.edit} onClose={() => setContactDlg({ open: false, edit: null })} onSaved={(np, msg, merged) => { applyServer(np); setContactDlg({ open: false, edit: null }); toast(merged ? CONFLICT_MSG.autoMerged : msg); }} /> : null}
      {branchDlg.open ? <BranchDialog partner={p} edit={branchDlg.edit} onClose={() => setBranchDlg({ open: false, edit: null })} onSaved={(np, msg, merged) => { applyServer(np); void qc.invalidateQueries({ queryKey: ["branches", p.id] }); setBranchDlg({ open: false, edit: null }); toast(merged ? CONFLICT_MSG.autoMerged : msg); }} /> : null}
      {node}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 支払条件（024）：締め日・支払月・支払日 → PUT /partners/:id/terms    */
/* ------------------------------------------------------------------ */
export const DAYS: TermDay[] = [5, 10, 15, 20, 25, 99];
export const MONTHS: TermMonth[] = [0, 1, 2];

/**
 * 支払条件の節（#267、Figma ② 278:53・278:75）：請求の形（sales の会社だけ）と 締め日・支払 の1行＋「修正」→ 窓「支払条件」。
 * 窓の中で 請求の形（都度｜締め・既定 都度）と 締め日・支払月・支払日 を選び「決定」で PUT /partners/:id/terms（版つき）
 */
function TermsEditor({ p, sales, onSaved }: { p: Partner; sales: boolean; onSaved: (p: Partner, merged: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const cur: PaymentTerms = (p.paymentTerms as PaymentTerms | null) ?? DEFAULT_PAYMENT_TERMS;
  return (
    <div data-testid={`terms-${p.id}`}>
      <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        {sales ? (
          <span className="chip quiet" data-testid="partner-billing-mode" title={CLOSING_MSG.billingMode}>
            {CLOSING_MSG.billingMode} {BILLING_MODE_LABEL[p.billingMode]}
          </span>
        ) : null}
        <span data-testid="partner-terms-label">
          {paymentTermsLabel(cur)}
          {p.paymentTerms ? "" : "（既定）"}
        </span>
        {sales && p.installments ? (
          <span className="chip quiet" data-testid="partner-installments" title={PAY_SCHEDULE_MSG.title}>
            {PAY_SCHEDULE_MSG.title} {installmentPlanLabel(p.installments)}
          </span>
        ) : null}
        <button type="button" className="small" onClick={() => setOpen(true)} data-testid="partner-terms-edit">
          修正
        </button>
      </div>
      {open ? <TermsDialog p={p} sales={sales} onClose={() => setOpen(false)} onSaved={(np, merged) => (setOpen(false), onSaved(np, merged))} /> : null}
    </div>
  );
}

function TermsDialog({ p, sales, onClose, onSaved }: { p: Partner; sales: boolean; onClose: () => void; onSaved: (p: Partner, merged: boolean) => void }) {
  const cur: PaymentTerms = (p.paymentTerms as PaymentTerms | null) ?? DEFAULT_PAYMENT_TERMS;
  const [v, setV] = useState<PaymentTerms>(cur);
  const [mode, setMode] = useState<BillingMode>(p.billingMode);
  // 分割払い（#267 3b、Figma 278:75）：スイッチ（既定 オフ）→ 回数・間隔。オフに戻しても回数・間隔は窓の中で覚える
  const [instOn, setInstOn] = useState(!!p.installments);
  const [inst, setInst] = useState<InstallmentPlan>(p.installments ?? { count: 3, intervalMonths: 1 });
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: () =>
      withPartnerVersion(p.id, p.updatedAt, (version) =>
        api<Partner>("PUT", `/partners/${p.id}/terms`, { ...v, ...(sales ? { billingMode: mode, installments: instOn ? inst : null } : {}), version }),
      ),
    onSuccess: ({ p: np, merged }) => onSaved(np, merged),
    onError: (e) => setError(messageOf(e)),
  });
  const close = () => !save.isPending && onClose();
  const sel = (id: string, label: string, value: number, opts: number[], fmt: (n: number) => string, set: (n: number) => void) => (
    <label className="row" style={{ gap: 6, alignItems: "center" }}>
      {label ? <span style={{ fontSize: "var(--fs-15)", color: "var(--ink2)" }}>{label}</span> : null}
      <select id={id} className="inp termsel" value={value} onChange={(e) => set(Number(e.target.value))}>
        {opts.map((o) => (
          <option key={o} value={o}>{fmt(o)}</option>
        ))}
      </select>
    </label>
  );
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="dlg" role="dialog" aria-label="支払条件" style={{ width: 560 }} data-testid="partner-terms-dialog" onKeyDown={(e) => e.key === "Escape" && close()}>
        <div className="dh">
          支払条件
          <div className="muted" style={{ fontSize: "var(--fs-15)", fontWeight: 400 }}>{p.name}</div>
        </div>
        <div className="db" style={{ display: "grid", gap: 16 }}>
          {sales ? (
            <div>
              <div className="row" style={{ gap: 6, alignItems: "center", marginBottom: 8 }}>
                <span id="pt_mode_l" style={{ fontSize: "var(--fs-15)", color: "var(--ink2)", fontWeight: 700 }}>{CLOSING_MSG.billingMode}</span>
                <Help testId="help-billing-mode" label={CLOSING_MSG.billingMode} text={t(CLOSING_MSG.billingModeHelp)} />
              </div>
              <Segmented labelledBy="pt_mode_l" options={(["per_delivery", "closing"] as const).map((k) => ({ value: k, label: BILLING_MODE_LABEL[k], testId: `pt-mode-${k}` }))} value={mode} onChange={setMode} />
            </div>
          ) : null}
          <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
            {sel("pt_c", "締め日", v.closingDay, DAYS, (n) => TERM_DAY_LABEL(n as TermDay), (n) => setV({ ...v, closingDay: n as TermDay }))}
            {sel("pt_m", "支払", v.paymentMonthOffset, MONTHS, (n) => TERM_MONTH_LABEL[n as TermMonth], (n) => setV({ ...v, paymentMonthOffset: n as TermMonth }))}
            {sel("pt_d", "", v.paymentDay, DAYS, (n) => TERM_DAY_LABEL(n as TermDay), (n) => setV({ ...v, paymentDay: n as TermDay }))}
          </div>
          {sales ? (
            <div style={{ borderTop: "1px solid var(--rule)", paddingTop: 16, display: "grid", gap: 12 }} data-testid="pt-installments">
              <div className="row" style={{ gap: 6, alignItems: "center" }}>
                <Switch checked={instOn} onChange={setInstOn} label={PAY_SCHEDULE_MSG.title} testId="pt-installments-on" />
                <Help testId="help-installments" label={PAY_SCHEDULE_MSG.title} text={t(PAY_SCHEDULE_MSG.help)} />
              </div>
              {instOn ? (
                <>
                  <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
                    {sel("pt_ic", PAY_SCHEDULE_MSG.count, inst.count, [...INSTALLMENT_COUNTS], PAY_SCHEDULE_MSG.countLabel, (n) => setInst({ ...inst, count: n }))}
                    {sel("pt_iv", PAY_SCHEDULE_MSG.interval, inst.intervalMonths, [...INSTALLMENT_INTERVALS], intervalLabel, (n) => setInst({ ...inst, intervalMonths: n }))}
                  </div>
                  <InstallmentExample terms={v} closing={mode === "closing"} plan={inst} />
                </>
              ) : null}
            </div>
          ) : null}
          <ErrorText>{error}</ErrorText>
        </div>
        <div className="df">
          <span className="sp" />
          <button type="button" className="btn" disabled={save.isPending} onClick={close} data-testid="partner-terms-cancel">取消</button>
          <button type="button" className="btn primary" disabled={save.isPending} onClick={() => save.mutate()} data-testid="partner-terms-save">決定</button>
        </div>
      </div>
    </div>
  );
}

/**
 * 支払条件の窓の分割払いの例（Figma 278:75）：締めなら「10月分の例」（今月の締め日の請求）、都度なら「10/31 請求の例」（今月末の請求）。
 * 期日は DB の fn_payment_due_date・fn_payment_schedule_plan と同じ決まりの契約の関数（発行で作る予定は DB が正）
 */
function InstallmentExample({ terms, closing, plan }: { terms: PaymentTerms; closing: boolean; plan: InstallmentPlan }) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const last = new Date(y, m, 0).getDate();
  const day = closing ? Math.min(terms.closingDay >= 99 ? last : terms.closingDay, last) : last;
  const bill = `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const dates = scheduleDatesOf(paymentDueOf(terms, bill), plan.count, plan.intervalMonths);
  const label = closing ? `${m}月分` : `${m}/${day} 請求`;
  const md = (d: string) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
  return (
    <div className="pt-example" data-testid="pt-installments-example">
      <span className="muted">{PAY_SCHEDULE_MSG.example(label)}</span>
      <b className="num">{dates.map(md).join("・")}</b>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 消費税の端数処理（#108、docs/41 ★4）→ PUT /partners/:id/tax-rounding。管理者だけ */
/* ------------------------------------------------------------------ */
function TaxRoundingEditor({ p, admin, onSaved, onError }: { p: Partner; admin: boolean | undefined; onSaved: (p: Partner, merged: boolean) => void; onError: (msg: string) => void }) {
  const co = useQuery({ queryKey: ROUNDING_KEY, queryFn: () => api<RoundingSettings>("GET", "/company/rounding") });
  const save = useMutation({
    mutationFn: (taxRounding: RoundingDirection | null) => withPartnerVersion(p.id, p.updatedAt, (version) => api<Partner>("PUT", `/partners/${p.id}/tax-rounding`, { taxRounding, version })),
    onSuccess: ({ p: np, merged }) => onSaved(np, merged),
    onError: (e) => onError(messageOf(e)),
  });
  return (
    <select
      id="pd_taxRounding"
      className="inp termsel"
      style={{ minWidth: 240 }}
      value={p.taxRounding ?? ""}
      disabled={!admin || save.isPending}
      data-testid="partner-tax-rounding"
      onChange={(e) => save.mutate(e.target.value ? RoundingDirection.parse(e.target.value) : null)}
    >
      <option value="">会社の既定に従う{co.data ? `（${ROUNDING_LABEL[co.data.taxRounding]}）` : ""}</option>
      {RoundingDirection.options.map((o) => (
        <option key={o} value={o}>{ROUNDING_LABEL[o]}</option>
      ))}
    </select>
  );
}

/* ------------------------------------------------------------------ */
/* 税を確定する書類（#266、docs/96 決めごと13）→ PUT /partners/:id/tax-on。管理者だけ。発行の瞬間に回に固まる（出した紙は変わらない） */
/* ------------------------------------------------------------------ */
function TaxOnEditor({ p, admin, onSaved, onError }: { p: Partner; admin: boolean | undefined; onSaved: (p: Partner, merged: boolean) => void; onError: (msg: string) => void }) {
  const L = useLabels();
  const save = useMutation({
    mutationFn: (taxOn: "invoice" | "delivery") => withPartnerVersion(p.id, p.updatedAt, (version) => api<Partner>("PUT", `/partners/${p.id}/tax-on`, { taxOn, version })),
    onSuccess: ({ p: np, merged }) => onSaved(np, merged),
    onError: (e) => onError(messageOf(e)),
  });
  return (
    <select id="pd_taxOn" className="inp termsel" style={{ minWidth: 200 }} value={p.taxOn ?? "invoice"} disabled={!admin || save.isPending} data-testid="partner-tax-on" onChange={(e) => save.mutate(e.target.value === "delivery" ? "delivery" : "invoice")}>
      <option value="invoice">請求書</option>
      <option value="delivery">{L.deliveryNote}</option>
    </select>
  );
}

/* ------------------------------------------------------------------ */
/* {acceptance}の既定（#266 1b）→ PUT /partners/:id/acceptance。だれでも。null＝会社に従う */
/* ------------------------------------------------------------------ */
function AcceptanceDefaultEditor({ p, onSaved, onError }: { p: Partner; onSaved: (p: Partner, merged: boolean) => void; onError: (msg: string) => void }) {
  const save = useMutation({
    mutationFn: (v: boolean | null) => withPartnerVersion(p.id, p.updatedAt, (version) => api<Partner>("PUT", `/partners/${p.id}/acceptance`, { acceptance: v, version })),
    onSuccess: ({ p: np, merged }) => onSaved(np, merged),
    onError: (e) => onError(messageOf(e)),
  });
  const cur = p.acceptanceDefault == null ? "follow" : p.acceptanceDefault ? "on" : "off";
  return (
    <Segmented
      labelledBy="pd_acceptance_l"
      options={[
        { value: "on", label: ACCEPTANCE_MSG.partnerOn },
        { value: "off", label: ACCEPTANCE_MSG.partnerOff },
        { value: "follow", label: ACCEPTANCE_MSG.partnerFollow },
      ]}
      value={cur}
      disabled={save.isPending}
      testId="partner-acceptance"
      onChange={(v) => save.mutate(v === "follow" ? null : v === "on")}
    />
  );
}

/* ------------------------------------------------------------------ */
/* 紙の書式（#78、044・046）：見積書・請求書・領収書 → PUT /partners/:id/paper */
/* ------------------------------------------------------------------ */
function PaperEditor({ p, admin, onSaved, onError }: { p: Partner; admin: boolean | undefined; onSaved: (p: Partner, merged: boolean) => void; onError: (msg: string) => void }) {
  const L = useLabels();
  const presets = useQuery({ queryKey: ["paper-presets"], queryFn: () => api<PaperPresetList>("GET", "/paper/presets") });
  const save = useMutation({
    mutationFn: (v: { quote?: string | null; invoice?: string | null; receipt?: string | null }) => withPartnerVersion(p.id, p.updatedAt, (version) => api<Partner>("PUT", `/partners/${p.id}/paper`, { ...v, version })),
    onSuccess: ({ p: np, merged }) => onSaved(np, merged),
    onError: (e) => onError(messageOf(e)),
  });
  const sel = (kind: "quote" | "invoice" | "receipt", label: string) => (
    <label className="row" style={{ gap: 6, alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-15)", color: "var(--ink2)" }}>{label}</span>
      <select id={`paper_${kind}`} className="inp termsel" style={{ minWidth: 200 }} value={p.paper[kind] ?? ""} disabled={!admin || save.isPending} onChange={(e) => save.mutate({ [kind]: e.target.value || null })} aria-label={`${label}の書式`}>
        <option value="">会社の既定に従う</option>
        {(presets.data?.presets ?? []).filter((x) => x.kinds.includes(kind)).map((x) => (
          <option key={x.key} value={`preset:${x.key}`}>{x.name}</option>
        ))}
        {(presets.data?.own[kind] ?? []).length ? (
          <optgroup label="カスタム書式">
            {(presets.data?.own[kind] ?? []).map((x) => (
              <option key={x.id} value={`template:${x.id}`}>{x.name}（{x.orient === "landscape" ? "横" : "縦"}）</option>
            ))}
          </optgroup>
        ) : null}
      </select>
    </label>
  );
  return (
    <div data-testid={`paper-${p.id}`}>
      <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
        {sel("quote", "見積書")}
        {sel("invoice", "請求書")}
        {sel("receipt", "領収書")}
      </div>
      {admin ? null : <div style={{ fontSize: "var(--fs-15)", color: "var(--ink2)", marginTop: 6 }}>{L.customer}ごとの書式を変えられるのは管理者だけです。</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 送付状の既定（#223 の2回目）：テンプレート・窓付封筒 → PUT /partners/:id/cover */
/* ------------------------------------------------------------------ */
function CoverDefaultsEditor({ p, disabled, onSaved, onError }: { p: Partner; disabled: boolean; onSaved: (p: Partner, merged: boolean) => void; onError: (msg: string) => void }) {
  const qc = useQueryClient();
  const M = COVER_LETTER_MSG;
  const settings = useQuery({ queryKey: ["cover-settings"], queryFn: () => api<CoverLetterSettings>("GET", "/cover-letters/settings") });
  const save = useMutation({
    mutationFn: (v: { templateId?: string | null; window?: boolean; mailMode?: "page" | "attach" | null }) => withPartnerVersion(p.id, p.updatedAt, (version) => api<Partner>("PUT", `/partners/${p.id}/cover`, { ...v, version })),
    onSuccess: ({ p: np, merged }) => onSaved(np, merged),
    onError: (e) => {
      void qc.invalidateQueries({ queryKey: ["partner", p.id] });
      onError(messageOf(e));
    },
  });
  /**
   * 押した瞬間に画面の値を変えてから送る（スイッチ・選ぶ欄は答えを待たない。断られたら読み直しで戻る）。
   * 画面の値はこの部品の state（React の押した操作の中で描き直る）。サーバーの値が変わったら合わせる
   */
  const [cur, setCur] = useState(p.cover);
  useEffect(() => setCur(p.cover), [p.cover.templateId, p.cover.window, p.cover.mailMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const put = (v: { templateId?: string | null; window?: boolean; mailMode?: "page" | "attach" | null }) => {
    setCur((c) => ({ templateId: v.templateId === undefined ? c.templateId : v.templateId, window: v.window ?? c.window, mailMode: v.mailMode === undefined ? c.mailMode : v.mailMode }));
    save.mutate(v);
  };
  const tpls = settings.data?.templates ?? [];
  const def = tpls.find((x) => x.isDefault);
  return (
    <GroupList>
      <GroupSelectRow
        id="pd_coverTemplate"
        label={M.partnerTemplate}
        value={cur.templateId ?? ""}
        options={[{ value: "", label: M.partnerTemplateCompany.replace("{name}", def?.name ?? "") }, ...tpls.map((x) => ({ value: x.id, label: x.name }))]}
        onChange={(v) => put({ templateId: v || null })}
        disabled={disabled || !settings.data}
        selectTestId="partner-cover-template"
      />
      <GroupSwitchRow label={M.partnerWindow} name={M.partnerWindow} checked={cur.window} onChange={(v) => put({ window: v })} disabled={disabled} testId="partner-cover-window" />
      {/* メールの渡し方（#225 ★1）：既定＝帳票は閲覧ページ（見積依頼書は添付） */}
      <GroupSelectRow
        id="pd_mailMode"
        label={MAIL_MSG.partnerMailMode}
        value={cur.mailMode ?? ""}
        options={[
          { value: "", label: MAIL_MSG.partnerMailModeDefault.replace("{mode}", p.partnerType === "vendor" ? MAIL_MODE_LABEL.attach : MAIL_MODE_LABEL.page) },
          { value: "page", label: MAIL_MODE_LABEL.page },
          { value: "attach", label: MAIL_MODE_LABEL.attach },
        ]}
        onChange={(v) => put({ mailMode: v === "page" || v === "attach" ? v : null })}
        disabled={disabled}
        selectTestId="partner-mail-mode"
      />
    </GroupList>
  );
}

/* ------------------------------------------------------------------ */
/* 担当者のダイアログ                                                    */
/* ------------------------------------------------------------------ */
function ContactDialog({ partner, edit, onClose, onSaved }: { partner: Partner; edit: PartnerContact | null; onClose: () => void; onSaved: (p: Partner, msg: string, merged: boolean) => void }) {
  const [v, setV] = useState<PartnerContactInput>({ name: edit?.name ?? "", department: edit?.department ?? "", title: edit?.title ?? "", mobile: edit?.mobile ?? "", email: edit?.email ?? "", isPrimary: edit?.isPrimary ?? partner.contacts.length === 0, branchId: edit?.branchId ?? null });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | undefined>();
  const [confirmDel, setConfirmDel] = useState(false);
  const onErr = (e: unknown) => {
    if (e instanceof ApiError && e.problem.errors) {
      setErrors(e.problem.errors);
      return;
    }
    setServerError(messageOf(e));
  };
  // #202：顧客の版を付けて送る（ほかで変わっていたら最新の版でもう一度。この窓の内容はそのまま）
  const save = useMutation({
    mutationFn: (body: PartnerContactInput) =>
      withPartnerVersion(partner.id, partner.updatedAt, (version) =>
        edit ? api<Partner>("PUT", `/partners/${partner.id}/contacts/${edit.id}`, { ...body, version }) : api<Partner>("POST", `/partners/${partner.id}/contacts`, { ...body, version }),
      ),
    onSuccess: ({ p, merged }) => onSaved(p, edit ? `担当者 ${v.name} を修正しました。` : `担当者 ${v.name} を追加しました。`, merged),
    onError: onErr,
  });
  const del = useMutation({
    mutationFn: () => withPartnerVersion(partner.id, partner.updatedAt, (version) => api<Partner>("DELETE", `/partners/${partner.id}/contacts/${edit!.id}?version=${encodeURIComponent(version)}`)),
    onSuccess: ({ p, merged }) => onSaved(p, `担当者 ${edit!.name} を消しました。`, merged),
    onError: onErr,
  });
  const busy = save.isPending || del.isPending;
  const submit = () => {
    setServerError(undefined);
    if (!v.name.trim()) {
      setErrors({ name: PARTNER_MSG.contactNameRequired });
      document.getElementById("cd_name")?.focus();
      return;
    }
    save.mutate(v);
  };
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="dlg" role="dialog" aria-label={edit ? "担当者編集" : "担当者追加"} onKeyDown={(e) => e.key === "Escape" && !busy && onClose()}>
        <div className="dh">{edit ? "担当者編集" : "担当者追加"}</div>
        <form
          className="db"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="row" style={{ alignItems: "flex-end" }}>
            <Field id="cd_name" label="氏名" style={{ flex: "2 1 200px" }}>
              <input id="cd_name" className="inp" autoFocus value={v.name} onKeyDown={guardComposingEnter} onChange={(e) => setV({ ...v, name: e.target.value })} aria-invalid={!!errors.name} />
            </Field>
            <Field id="cd_dept" label="部署（任意）" style={{ flex: "1 1 140px" }}>
              <input id="cd_dept" className="inp" value={v.department} onKeyDown={guardComposingEnter} onChange={(e) => setV({ ...v, department: e.target.value })} />
            </Field>
            <Field id="cd_title" label="肩書（任意）" style={{ flex: "1 1 140px" }}>
              <input id="cd_title" className="inp" value={v.title} onKeyDown={guardComposingEnter} onChange={(e) => setV({ ...v, title: e.target.value })} />
            </Field>
          </div>
          <div className="row" style={{ alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 250px" }}>
              <PhoneFields id="cd_mobile" label="携帯（任意）" value={v.mobile} onChange={(mobile) => setV((cur) => ({ ...cur, mobile }))} invalid={!!errors.mobile} />
            </div>
            <Field id="cd_email" label="メール（任意）" style={{ flex: "1 1 220px" }}>
              <input id="cd_email" className="inp" type="email" inputMode="email" value={v.email} onKeyDown={guardComposingEnter} onChange={(e) => setV({ ...v, email: e.target.value })} aria-invalid={!!errors.email} />
            </Field>
          </div>
          <div className="row" style={{ alignItems: "flex-end" }}>
            {partner.branches.length ? (
              <Field id="cd_branch" label="所属" style={{ flex: "1 1 200px" }}>
                <select id="cd_branch" className="inp" value={v.branchId ?? ""} onChange={(e) => setV({ ...v, branchId: e.target.value || null })}>
                  <option value="">会社（本社）</option>
                  {partner.branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </Field>
            ) : null}
            <label className="row" style={{ gap: 8, minHeight: 44 }}>
              <input type="checkbox" checked={v.isPrimary} disabled={!!edit?.isPrimary} onChange={(e) => setV({ ...v, isPrimary: e.target.checked })} style={{ width: 22, height: 22 }} />
              <span>主担当（一覧に出す1人）</span>
            </label>
          </div>
          <ErrorText>{serverError ?? Object.values(errors)[0]}</ErrorText>
          <div className="df" style={{ padding: 0, borderTop: 0 }}>
            {edit ? (
              confirmDel ? (
                <span className="row">
                  <button type="button" className="btn shu" disabled={busy} onClick={() => del.mutate()}>削除</button>
                  <button type="button" className="btn quiet" onClick={() => setConfirmDel(false)}>取消</button>
                </span>
              ) : (
                <button type="button" className="small shu" disabled={busy} onClick={() => setConfirmDel(true)}>担当者削除…</button>
              )
            ) : (
              <span />
            )}
            <span className="row">
              <button type="button" className="btn quiet" disabled={busy} onClick={onClose}>取消</button>
              <button type="submit" className="btn primary" disabled={busy} data-testid="contact-save">担当者保存</button>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 支店・営業所のダイアログ                                              */
/* ------------------------------------------------------------------ */
function BranchDialog({ partner, edit, onClose, onSaved }: { partner: Partner; edit: PartnerBranch | null; onClose: () => void; onSaved: (p: Partner, msg: string, merged: boolean) => void }) {
  const [v, setV] = useState<PartnerBranchInput>({ name: edit?.name ?? "", postalCode: edit?.postalCode ?? "", address: edit?.address ?? "", building: edit?.building ?? "", phone: edit?.phone ?? "", fax: edit?.fax ?? "", branchCode: edit?.branchCode ?? "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | undefined>();
  const [confirmDel, setConfirmDel] = useState(false);
  const onErr = (e: unknown) => {
    if (e instanceof ApiError && e.problem.errors) {
      setErrors(e.problem.errors);
      return;
    }
    setServerError(messageOf(e));
  };
  // #202：顧客の版を付けて送る（ほかで変わっていたら最新の版でもう一度。この窓の内容はそのまま）
  const save = useMutation({
    mutationFn: (body: PartnerBranchInput) =>
      withPartnerVersion(partner.id, partner.updatedAt, (version) =>
        edit ? api<Partner>("PUT", `/partners/${partner.id}/branches/${edit.id}`, { ...body, version }) : api<Partner>("POST", `/partners/${partner.id}/branches`, { ...body, version }),
      ),
    onSuccess: ({ p, merged }) => onSaved(p, edit ? `${v.name} を修正しました。` : `${v.name} を追加しました。見積・請求書の紙の上で選べます。`, merged),
    onError: onErr,
  });
  const del = useMutation({
    mutationFn: () => withPartnerVersion(partner.id, partner.updatedAt, (version) => api<Partner>("DELETE", `/partners/${partner.id}/branches/${edit!.id}?version=${encodeURIComponent(version)}`)),
    onSuccess: ({ p, merged }) => onSaved(p, `${edit!.name} を「使わない」にしました。出した紙の宛名はそのままです。`, merged),
    onError: onErr,
  });
  const busy = save.isPending || del.isPending;
  const submit = () => {
    setServerError(undefined);
    if (!v.name.trim()) {
      setErrors({ name: PARTNER_MSG.branchNameRequired });
      document.getElementById("bd_name")?.focus();
      return;
    }
    save.mutate(v);
  };
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="dlg" role="dialog" aria-label={edit ? "支店・営業所編集" : "支店・営業所追加"} onKeyDown={(e) => e.key === "Escape" && !busy && onClose()}>
        <div className="dh">{edit ? "支店・営業所編集" : "支店・営業所追加"}</div>
        <form
          className="db"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Field id="bd_name" label={<>支店・営業所の名前（紙の宛名に「{partner.name}」の下に出ます）</>}>
            <input id="bd_name" className="inp" autoFocus value={v.name} onKeyDown={guardComposingEnter} onChange={(e) => setV({ ...v, name: e.target.value })} aria-invalid={!!errors.name} />
          </Field>
          {/* 支店コード（#288）：任意。取引先の中で一意 */}
          <Field id="bd_branchCode" label={PARTNER_PICKER_MSG.branchCode} style={{ maxWidth: 240 }}>
            <input id="bd_branchCode" className="inp code" value={v.branchCode ?? ""} maxLength={40} autoCapitalize="characters" onKeyDown={guardComposingEnter} onChange={(e) => setV({ ...v, branchCode: e.target.value })} onBlur={() => setV((cur) => ({ ...cur, branchCode: partnerCodeOf(cur.branchCode ?? "") }))} aria-invalid={!!errors.branchCode} data-testid="branch-code" />
          </Field>
          <AddressFields value={{ postalCode: v.postalCode, address: v.address, building: v.building }} onChange={(a) => setV({ ...v, ...a })} ids={{ zip: "bd_zip", address: "bd_addr", building: "bd_bldg" }} />
          <div className="tel-stack">
            <PhoneFields id="bd_phone" label="電話（任意）" value={v.phone} onChange={(phone) => setV((cur) => ({ ...cur, phone }))} />
            <PhoneFields id="bd_fax" label="FAX（任意）" value={v.fax} onChange={(fax) => setV((cur) => ({ ...cur, fax }))} />
          </div>
          <ErrorText>{serverError ?? Object.values(errors)[0]}</ErrorText>
          <div className="df" style={{ padding: 0, borderTop: 0 }}>
            {edit ? (
              confirmDel ? (
                <span className="row">
                  <button type="button" className="btn shu" disabled={busy} onClick={() => del.mutate()}>使用停止</button>
                  <button type="button" className="btn quiet" onClick={() => setConfirmDel(false)}>取消</button>
                </span>
              ) : (
                <button type="button" className="small shu" disabled={busy} onClick={() => setConfirmDel(true)}>支店使用停止…</button>
              )
            ) : (
              <span />
            )}
            <span className="row">
              <button type="button" className="btn quiet" disabled={busy} onClick={onClose}>取消</button>
              <button type="submit" className="btn primary" disabled={busy} data-testid="branch-save">支店・営業所保存</button>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
